<script>
import Layout from '@layouts/main.vue'
import baseMixin from '@/mixins/baseMixin'
import idleMixin from '@/mixins/idleMixin'
import { mapActions, mapGetters } from 'vuex'
import BookingCreateMeta from '@/components/booking/booking-create-meta.vue'
import BookingLocationList from '@/components/booking/booking-location-list.vue'
import { isEmpty } from 'lodash'
import PromiseDialogOld from '@/components/general/promise-dialog-old.vue'
import BookingCreateSummaryCard from '@/components/booking/booking-create-summary-card.vue'
import dayjs from '@/services/date'
import routeDefinitions from '@/shared/constants/routes/routeDefinitions'
import EditBookingSkeleton from '@/components/skeleton/edit-booking-skeleton.vue'
import PromiseConfirmDialog from '@/components/general/promise-confirm-dialog.vue'
import { EDIT_UNSAVED_DIALOG_ACTIONS } from '@/shared/constants/dialog/EditDialogActions'

export default {
  name: 'BookingCreateView',
  metaInfo() {
    return {
      title: this.$route?.meta?.title,
      meta: [{ name: 'description', content: 'The Booking Create page.' }],
    }
  },
  components: {
    Layout,
    BookingCreateMeta,
    BookingLocationList,
    BookingCreateSummaryCard,
    PromiseDialogOld,
    EditBookingSkeleton,
    PromiseConfirmDialog,
  },
  mixins: [baseMixin, idleMixin],
  data: () => {
    return {
      selectedLocation: null,
      formChanged: false,
      editFormChanged: false,
      showSummaryPage: false,
      summaryData: null,
      preselectDate: null,
      isEditMode: false,
      editModeType: null,
      bookingId: null,
      booking: null,
      loadingBooking: false,
    }
  },
  computed: {
    ...mapGetters({
      locations: 'locations/bookingLocations',
    }),
  },
  watch: {
    idleMixin_isIdle(v, o) {
      this.loadLocationBookingBlocks()
    },
  },
  async created() {
    this.validatePreselectDate()

    const { name, query } = this.$route
    const { bookingsEdit, bookingsCreate } = routeDefinitions

    const isEditRoute = name === bookingsEdit.name
    const isCreateRoute = name === bookingsCreate.name

    if ((isEditRoute || isCreateRoute) && query.mode) {
      this.loadingBooking = true
    }
    this.loadLocationBookingBlocks()
    const result = await this.loadBookingLocations()

    // Auto select location if only one was returned
    if (this.hasSingleLocation(result)) this.selectLocation(result.data[0])

    if ((isEditRoute || isCreateRoute) && query.mode) {
      this.isEditMode = isEditRoute
      this.bookingMode = query.mode
      this.bookingId = query.bookingId

      if (isEditRoute) {
        this.preselectDate = query.date
      } else {
        if(query.date){
          const dateQuery = dayjs(query.date)
          if (dateQuery.isAfter(dayjs())) {
            this.preselectDate = query.date
          }
        }
      }
      await this.loadBookingDetails(this.bookingId)
    }
  },
  async beforeRouteLeave(to, from, next) {
    // --- Edit mode ---
    if (this.isEditMode) {
      if (!this.editFormChanged) return next()

      const result = await this.showEditUnsavedDialog()

      if (result === 'save') {
        next(false)
        this.$nextTick(() => {
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: 'smooth',
          })
        })
        this.editFormChanged = false
        await this.$refs.bookingCreateMeta.submit()
        return
      }

      if (result === 'discard') {
        this.editFormChanged = false
        return next()
      }

      return next(false)
    }

    // --- Create mode ---
    if (!this.formChanged) return next()

    const result = await this.showCreateUnsavedDialog()
    if (result === 'ok') return next()

    next(false)
  },
  methods: {
    ...mapActions({
      loadBookingLocations: 'locations/loadBookingLocations',
      loadLocationBookingBlocks: 'locations/loadLocationBookingBlocks',
      setRecentLocation: 'bookings/setRecentLocation',
      getBookingDetailById: 'bookings/getBookingDetailById',
    }),
    async showEditUnsavedDialog() {
      return await this.$refs.editFormUnsavedChangesDialog.show({
        title: this.$t('general.unsavedChangesDialogConfirmation.title'),
        text: this.$t('general.unsavedChangesDialogConfirmation.desc'),
        icon: 'mdi-alert-outline',
        iconClass: 'icon-warning',
        actions: EDIT_UNSAVED_DIALOG_ACTIONS(this.$t.bind(this)),
      })
    },
    async showCreateUnsavedDialog() {
      return await this.$refs.unsavedChangesDialog.show({
        text: this.$t('general.discardDialogBodyText'),
        title: this.$t('general.discardDialogTitle'),
        actions: {
          cancel: this.$t('general.cancel'),
          confirm: {
            text: this.$t('general.discardDialogDiscardBtnText'),
            color: 'red',
          },
        },
        icon: false,
        persistant: true,
      })
    },
    async loadBookingDetails(bookingId) {
      try {
        const response = await this.getBookingDetailById(bookingId)
        if (response && response.data) {
          this.booking = response.data
          const bookingLocation = this.locations.find(
            (x) => x.locationId === this.booking.location.id
          )
          if (bookingLocation) {
            this.selectLocation(bookingLocation)
          }
        }
      } finally {
        this.loadingBooking = false
      }
    },
    validatePreselectDate() {
      if (
        !this.$route.query ||
        !Object.prototype.hasOwnProperty.call(this.$route.query, 'date')
      )
        return

      const dateQuery = dayjs(this.$route.query.date)
      const isPast = dateQuery.isBefore(dayjs())

      if (isPast) return
      this.preselectDate = this.$route.query.date
    },
    selectLocation(location) {
      this.selectedLocation = location
      this.setRecentLocation(location.locationId)
    },
    changeLocation() {
      this.selectedLocation = null
    },
    hasSingleLocation(resultObj) {
      return (
        resultObj.isSuccess &&
        !isEmpty(resultObj.data) &&
        Array.isArray(resultObj.data) &&
        resultObj.data.length === 1
      )
    },
    handleDisplaySummaryPage(summaryObj) {
      this.formChanged = false
      this.summaryData = summaryObj
      this.showSummaryPage = true
    },
    createNewBooking() {
      this.showSummaryPage = false
      if (this.hasSingleLocation({ isSuccess: true, data: this.locations }))
        this.selectLocation(this.locations[0])
      else this.changeLocation()
      this.summaryData = null
    },
    async handleBackClick() {
      // --- Edit mode ---
      if (this.isEditMode) {
        if (!this.editFormChanged) {
          this.handlePrevious()
          return
        }

        const result = await this.showEditUnsavedDialog()

        if (result === 'save') {
          this.$nextTick(() => {
            window.scrollTo({
              top: document.body.scrollHeight,
              behavior: 'smooth',
            })
          })
          this.editFormChanged = false
          await this.$refs.bookingCreateMeta.submit()
          return
        }

        if (result === 'discard') {
          this.editFormChanged = false
          this.handlePrevious()
          return
        }

        return
      }

      // --- Create mode ---
      if (!this.formChanged) {
        this.handlePrevious()
        return
      }

      const result = await this.showCreateUnsavedDialog()
      if (result === 'ok') {
        this.formChanged = false
        this.handlePrevious()
      }
    },
    handlePrevious() {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.push({ name: routeDefinitions.home.name })
      }
    },
  },
}
</script>

<template>
  <Layout>
    <!-- Skeleton loader while fetching booking -->
    <template v-if="loadingBooking">
      <EditBookingSkeleton />
    </template>
    <template v-else>
      <BookingCreateSummaryCard
        v-if="showSummaryPage"
        :edit-mode="isEditMode"
        :booking-mode="bookingMode"
        :summary-data="summaryData"
        @createNewBooking="createNewBooking"
      />
      <BookingLocationList
        v-else-if="!selectedLocation"
        @selectLocation="selectLocation"
      />
      <BookingCreateMeta
        v-else
        ref="bookingCreateMeta"
        :selected-location="selectedLocation"
        :preselect-date="preselectDate"
        :edit-mode="isEditMode"
        :booking-mode="bookingMode"
        :booking="booking"
        @showSummaryPage="handleDisplaySummaryPage"
        @changeLocation="changeLocation"
        @updated="formChanged = true"
        @backClicked="handleBackClick"
        @editChanged="editFormChanged = $event"
      />
      <PromiseDialogOld ref="unsavedChangesDialog" />
      <PromiseConfirmDialog ref="editFormUnsavedChangesDialog" :width="'544'" />
    </template>
  </Layout>
</template>
